import React, { useState, useMemo } from 'react'
import { ImportExportContainer } from '../../types/container'
import { Table } from '../ui/Table/Table'
import { TableHeader } from '../ui/Table/TableHeader'
import { TableRow } from '../ui/Table/TableRow'
import { TableCell } from '../ui/Table/TableCell'

export interface ImportExportContainerTableProps {
  data: ImportExportContainer[]
  onRowClick: (container: ImportExportContainer) => void
}

export const ImportExportContainerTable: React.FC<ImportExportContainerTableProps> = ({ data, onRowClick }) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(25)

  const totalPages = Math.ceil(data.length / rowsPerPage)
  const startIndex = (currentPage - 1) * rowsPerPage
  const paginatedData = data.slice(startIndex, startIndex + rowsPerPage)

  const pageNumbers = useMemo(() => {
    const pages: number[] = []
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i)
    }
    return pages
  }, [totalPages])

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page)
    }
  }

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(Number(event.target.value))
    setCurrentPage(1)
  }

  return (
    <div className="space-y-4">
      <Table>
        <TableHeader columns={5}>
          <TableCell>Containernummer</TableCell>
          <TableCell>Container type</TableCell>
          <TableCell>Rederij</TableCell>
          <TableCell>Vol/leeg</TableCell>
          <TableCell>Chauffeur</TableCell>
        </TableHeader>

        {paginatedData.map((container) => (
          <TableRow
            key={container.ritNr}
            columns={5}
            onClick={() => onRowClick(container)}
          >
            <TableCell truncate>{container.containerNumber}</TableCell>
            <TableCell truncate>{container.containerType}</TableCell>
            <TableCell truncate>{container.shippingLine}</TableCell>
            <TableCell truncate>{container.volLeeg || '-'}</TableCell>
            <TableCell truncate>{container.chauffeur}</TableCell>
          </TableRow>
        ))}

        {paginatedData.length === 0 && (
          <div className="px-8 py-12 text-center text-gray-500">
            Geen containers gevonden
          </div>
        )}
      </Table>

      {/* Pagination Controls */}
      <div className="flex items-center justify-between px-4">
        <div className="flex items-center gap-2 text-sm text-gray-700">
          <span>Rijen per pagina:</span>
          <select
            value={rowsPerPage}
            onChange={handleRowsPerPageChange}
            className="border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
        </div>

        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-700">
            Pagina {currentPage} van {totalPages || 1}
          </span>

          <div className="flex gap-1">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Vorige
            </button>

            {pageNumbers.map((page) => (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`px-3 py-1 text-sm border rounded ${
                  page === currentPage
                    ? 'text-white'
                    : 'border-gray-300 hover:bg-gray-50'
                }`}
                style={page === currentPage ? { backgroundColor: '#DA0000', borderColor: '#DA0000' } : {}}
              >
                {page}
              </button>
            ))}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages || totalPages === 0}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Volgende
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
